import React from 'react';
import { AdminCheckoutLineItem, AdminCheckoutUserGroup } from '../../types/adminCheckout';
import { formatSubscriptionDate, getSportLabel } from '../../utils/admin/formatSubscriptionDate';

interface CheckoutListTableProps {
  groups: AdminCheckoutUserGroup[];
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$', eur: '€', gbp: '£', inr: '₹', jpy: '¥', cad: 'CA$', aud: 'A$',
};

function formatAmount(amount: number, currency: string): string {
  const c = (currency || 'usd').toLowerCase();
  const symbol = CURRENCY_SYMBOLS[c] ?? `${c.toUpperCase()} `;
  if (!Number.isFinite(amount)) return '—';
  const hasCents = Math.abs(amount % 1) > 0;
  return `${symbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatLineType(lineType: string): string {
  const t = (lineType || 'sport').toLowerCase();
  if (t === 'addon') return 'Addon';
  return 'Sport';
}

function formatSportCell(line: AdminCheckoutLineItem): string {
  if (line.line_type?.toLowerCase() === 'addon') {
    return line.addon_label?.trim() || '—';
  }
  if (line.sport_code?.trim()) {
    return getSportLabel(line.sport_code);
  }
  return '—';
}

function formatDateTime(value: string | null): string {
  if (!value?.trim()) return '—';
  return formatSubscriptionDate(value);
}

const CheckoutListTable: React.FC<CheckoutListTableProps> = ({ groups }) => {
  if (groups.length === 0) {
    return (
      <div className="text-center p-10 bg-gray-50 rounded-lg text-gray-500">
        No checkout payments found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub start</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub end</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.map((group) => {
            const lineCount = Math.max(group.lines.length, 1);
            const lines = group.lines.length > 0 ? group.lines : [null];

            return lines.map((line, lineIndex) => (
              <tr
                key={`${group.billing_user_id}-${line?.payment_id ?? 'empty'}-${line?.sport_code ?? lineIndex}`}
                className="hover:bg-gray-50 align-top"
              >
                {lineIndex === 0 && (
                  <>
                    <td className="px-3 py-3 border-r border-gray-100" rowSpan={lineCount}>
                      {group.full_name?.trim() ? (
                        <div className="font-medium text-gray-900">{group.full_name}</div>
                      ) : null}
                      <div className={`text-sm ${group.full_name?.trim() ? 'text-gray-600' : 'font-medium text-gray-900'}`}>
                        {group.username}
                      </div>
                      <div className="text-xs text-gray-500">{group.email?.trim() ? group.email : '—'}</div>
                      {group.latest_checkout_at?.trim() ? (
                        <div className="mt-1 text-xs text-gray-500">
                          Last checkout {formatDateTime(group.latest_checkout_at)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 border-r border-gray-100" rowSpan={lineCount}>
                      {group.team_name?.trim() ? group.team_name : '—'}
                    </td>
                  </>
                )}
                {line ? (
                  <>
                    <td className="px-3 py-3 text-gray-700">{formatLineType(line.line_type)}</td>
                    <td className="px-3 py-3 text-gray-800 font-medium">{formatSportCell(line)}</td>
                    <td className="px-3 py-3 text-gray-900 tabular-nums whitespace-nowrap">
                      {formatAmount(line.amount, line.currency)}
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Paid
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                      {formatDateTime(line.subscription_start)}
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                      {formatDateTime(line.subscription_end)}
                    </td>
                  </>
                ) : (
                  <td colSpan={6} className="px-3 py-3 text-gray-400 text-sm">
                    No line items
                  </td>
                )}
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CheckoutListTable;
